import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type ApiError, type AskDataResult } from '@whinerp/client';
import { formatCount } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Ask your data (G-215): a question in words, answered as a report - with
 * how it was understood said first, so a chart answering a different question
 * is caught before anybody quotes it.
 */
export function AskPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();
  const { can } = useSession();
  const [question, setQuestion] = useState('');
  const ask = useMutation<AskDataResult, ApiError>({ mutationFn: () => api.askData({ body: { question } }) });
  const save = useMutation<{ reportId: string }, ApiError, AskDataResult>({
    mutationFn: (answer) => api.saveReport({ body: { name: answer.interpretation, spec: answer.spec, visibility: 'private' } }),
    onSuccess: (saved) => navigate(`/reports/${saved.reportId}`),
  });
  if (!can('analytics.view')) return null;
  const answer = ask.data;
  const values = answer?.result.groups.map((group) => Number(group.values[0] ?? group.count)) ?? [];
  const peak = Math.max(1, ...values.map((value) => Math.abs(value)));
  return (
    <Panel data-region="ask-data">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('ask.title')}</SectionLabel>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (question.trim()) ask.mutate();
          }}
        >
          <input
            data-ask-question
            aria-label={t('ask.title')}
            placeholder={t('ask.placeholder')}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            className="h-input min-w-0 flex-1 rounded-control border border-control bg-surface px-3 text-body text-ink"
          />
          <Button intent="primary" type="submit" data-ask-submit disabled={!question.trim() || ask.isPending}>{t('ask.submit')}</Button>
        </form>
        {ask.error ? <p data-ask-refusal className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">{ask.error.message}</p> : null}
        {answer ? (
          <div className="flex flex-col gap-2" data-ask-answer>
            <p className="text-body text-ink" data-ask-interpretation>{answer.interpretation}</p>
            <p className="text-meta text-ink-meta" data-ask-by={answer.answeredBy}>
              {answer.answeredBy === 'model' ? t('ask.byModel') : t('ask.byKeywords')}
              {answer.modelProblem ? ` ${t('ask.modelProblem', { values: { problem: answer.modelProblem } })}` : ''}
            </p>
            <ul className="flex flex-col gap-1">
              {answer.result.groups.map((group, index) => (
                <li key={group.label} data-ask-group={group.label} className="flex items-center gap-2 text-dense">
                  <span className="w-40 shrink-0 truncate text-ink">{group.label}</span>
                  <span className="h-2 rounded-full bg-bronze" style={{ width: `${String(Math.max(2, Math.round((Math.abs(values[index] ?? 0) / peak) * 100)))}%` }} aria-hidden />
                  <span className="ms-auto text-ink" data-ask-value>{group.values[0] ?? formatCount(group.count, format)}</span>
                </li>
              ))}
            </ul>
            <div>
              <Button intent="secondary" size="small" data-ask-save disabled={save.isPending} onClick={() => save.mutate(answer)}>{t('ask.save')}</Button>
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
